import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Banknote, Loader2 } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { businessApi } from '@/lib/api';
import { toast } from 'sonner';

export function PaymentInstructionsSettings() {
  const { nav } = useBusinessNav();
  const { currentBusiness, refreshUser } = useApp();
  const [instructions, setInstructions] = useState(currentBusiness?.payment_instructions ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!currentBusiness) return;

    setSaving(true);
    const response = await businessApi.update(currentBusiness.id, {
      payment_instructions: instructions,
    } as Record<string, unknown>);

    if (response.data) {
      await refreshUser();
      toast.success('Payment instructions saved');
    } else {
      toast.error('Failed to save payment instructions');
    }
    setSaving(false);
  };

  const hasChanges = instructions !== (currentBusiness?.payment_instructions ?? '');

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Payment Instructions</h1>
        <p className="text-sm text-muted-foreground">
          Add instructions that appear on your invoices and quotes.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-100 dark:bg-lime-950 rounded-full flex items-center justify-center">
              <Banknote className="w-5 h-5 text-lime-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Payment Instructions</CardTitle>
              <CardDescription>
                Tell clients how they can pay you
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Enter payment instructions..."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={6}
            className="resize-none"
          />
          <p className="text-sm text-muted-foreground">
            Examples: bank transfer details, check mailing address, accepted payment methods,
            or any special instructions for your clients.
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="w-full sm:w-auto"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Instructions
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
